import { StrongifierCompletedWorkoutV2 } from 'src/models/strongifier-completed-workout-v2';
import { StrongifierCompletedActivity } from 'src/models/strongifier-completed-activity';
// Base API server URL from environment
const apiServerUrl = process.env.REACT_APP_API_SERVER_URL;

interface UploadActivityResponse {
  data?: StrongifierCompletedActivity;
  error?: { message: string };
}

/**
 * Upload a .fit file to the server
 */
export async function uploadActivity(
  accessToken: string,
  file: File,
  activityName: string,
  timezone?: string
): Promise<UploadActivityResponse> {
  try {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('activity_name', activityName);
    if (timezone) {
      formData.append('client_tz', timezone);
    }

    const res = await fetch(`${apiServerUrl}/api/activities/upload`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        // Don't set Content-Type - let browser set it for multipart/form-data
      },
      body: formData,
    });

    if (!res.ok) {
      const errBody = await res.json().catch(() => ({ message: res.statusText }));
      return { error: { message: errBody.message } };
    }

    const data: StrongifierCompletedActivity = await res.json();
    console.log('Activity uploaded successfully:', data);
    return { data };
  } catch (e: any) {
    return { error: { message: e.message } };
  }
}

interface UpdateActivityNameResponse {
  data?: StrongifierCompletedActivity;
  error?: { message: string };
}

/**
 * Update the name of an activity
 */
export async function updateActivityName(
  accessToken: string,
  id: string,
  strongifier_activity_name: string
): Promise<UpdateActivityNameResponse> {
  try {
    const res = await fetch(`${apiServerUrl}/api/activities/update`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ id, strongifier_activity_name: strongifier_activity_name.trim() })
    });

    if (!res.ok) {
      const body = await res.json().catch(() => ({ detail: res.statusText }));
      return { error: { message: body.detail || body.message || 'Update failed' } };
    }

    const data: StrongifierCompletedActivity = await res.json();
    return { data };
  } catch (e: any) {
    return { error: { message: e.message } };
  }
}

interface DeleteActivityResponse {
  ok: boolean;
  error?: { message: string };
}

/**
 * Delete a completed activity by Strongifier ID
 */
export async function deleteActivity(
  accessToken: string,
  strongifier_activity_id: string
): Promise<DeleteActivityResponse> {
  try {
    const res = await fetch(`${apiServerUrl}/api/activities/delete`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ strongifier_activity_id }),
    });

    if (!res.ok) {
      const body = await res.json().catch(() => ({ error: res.statusText }));
      return { ok: false, error: { message: body.error || 'Delete failed' } };
    }

    // Optional: validate { status: 'deleted' }
    // const body = await res.json().catch(() => ({}));
    return { ok: true };
  } catch (e: any) {
    return { ok: false, error: { message: e.message } };
  }
}

interface CompletedWorkoutsResponse {
  data: StrongifierCompletedWorkoutV2[];
  error?: { message: string };
}

/**
 * Fetch the list of workouts (newest→oldest).
 */
export async function getCompletedWorkouts(
  accessToken: string
): Promise<CompletedWorkoutsResponse> {
  try {
    const res = await fetch(`${apiServerUrl}/api/workouts/completed`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
    });

    if (!res.ok) {
      const errBody = await res.json().catch(() => ({ message: res.statusText }));
      return { data: [], error: { message: errBody.message } };
    }

    const data: StrongifierCompletedWorkoutV2[] = await res.json();
    return { data };
  } catch (e: any) {
    return { data: [], error: { message: e.message } };
  }
}

interface WorkoutResponse {
  data?: StrongifierCompletedWorkoutV2;
  error?: { message: string };
}

export interface WeeklyStatsActuals {
  workouts: number;
  distance_km: number;
  duration_minutes: number;
  /** Leg-heavy time that counts as training: zones 1-5 when HR data exists,
   *  full duration for runs/hikes/rides without it, nothing for walks. */
  training_minutes: number;
  hr_covered_minutes: number;
  zone_seconds: Record<string, number>;
  /** This week's long-classified workout (longest if several), or null. */
  long_effort?: { name: string | null; duration_minutes: number } | null;
}

export interface WeeklyStatsIdeals {
  available: boolean;
  reason?: string;
  phase?: string;
  weeks_until_race?: number | null;
  target_duration_hours?: number;
  zone_percentages?: Record<string, number>;
  zone_target_seconds?: Record<string, number>;
  long_effort_target_seconds?: number;
  long_effort_zone?: string;
}

export interface WeeklyStats {
  actuals: WeeklyStatsActuals;
  /** Zone number -> bpm range from the most recent workout with zone stats. */
  zone_hr_ranges?: Record<string, string>;
  ideals: WeeklyStatsIdeals;
}

/**
 * This week's actuals plus the phase-derived ideal week.
 */
export async function getWeeklyStats(
  accessToken: string,
  timezone: string
): Promise<{ data?: WeeklyStats; error?: { message: string } }> {
  try {
    const res = await fetch(
      `${apiServerUrl}/api/weekly-stats?timezone=${encodeURIComponent(timezone)}`,
      {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
      }
    );
    if (!res.ok) {
      const errBody = await res.json().catch(() => ({ message: res.statusText }));
      return { error: { message: errBody.message || res.statusText } };
    }
    return { data: await res.json() };
  } catch (e: any) {
    return { error: { message: e.message } };
  }
}

export interface ManualWorkoutPayload {
  activity_type: string;
  local_datetime: string; // ISO wall time, no offset
  timezone: string;
  duration_minutes: number;
  distance_km: number;
  workout_name?: string;
  average_heart_rate?: number;
}

/**
 * Log a manually-entered workout (dual-writes through both engines).
 */
export async function logManualWorkout(
  accessToken: string,
  payload: ManualWorkoutPayload
): Promise<WorkoutResponse> {
  try {
    const res = await fetch(`${apiServerUrl}/api/workouts/manual`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    if (!res.ok) {
      const errBody = await res.json().catch(() => ({ message: res.statusText }));
      return { error: { message: errBody.message || res.statusText } };
    }

    const data: StrongifierCompletedWorkoutV2 = await res.json();
    return { data };
  } catch (e: any) {
    return { error: { message: e.message } };
  }
}

/**
 * Fetch one workout with its full point-in-time analysis state.
 */
export async function getWorkout(
  accessToken: string,
  workoutId: string
): Promise<WorkoutResponse> {
  try {
    const res = await fetch(
      `${apiServerUrl}/api/workouts/${encodeURIComponent(workoutId)}`,
      {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
      }
    );

    if (!res.ok) {
      const errBody = await res.json().catch(() => ({ message: res.statusText }));
      return { error: { message: errBody.message || res.statusText } };
    }

    const data: StrongifierCompletedWorkoutV2 = await res.json();
    return { data };
  } catch (e: any) {
    return { error: { message: e.message } };
  }
}
